import * as React from "react";
import { useState, useEffect } from "react";
import { Stack, Text, Spinner, SpinnerSize, Link, DefaultButton, getTheme } from "@fluentui/react";
import { apiBaseUrl } from "../../../auth/authConfig";
import { authHeaders } from "../../../auth/authDialog";
import { BankInfo } from "../types";
import BankInfoCard, { FieldRow } from "../BankInfoCard";
import { openBranchMap } from "../../mapDialog";

const API_URL = apiBaseUrl();
const theme = getTheme();

type MetricPoint = {
  value: number;
  date: string;
};

// Latest observation for a field, from the history endpoint (values are latest-first).
async function fetchLatestMetric(cert: number, field: string): Promise<MetricPoint | null> {
  const response = await fetch(
    `${API_URL}/banks/${cert}/metric/${encodeURIComponent(field)}`,
    { headers: authHeaders() }
  );
  if (response.status === 401 || response.status === 403) {
    throw new Error("subscription");
  }
  if (!response.ok) return null;
  const data: { values?: { date: string; value: number }[] } = await response.json();
  if (!data.values || data.values.length === 0) return null;
  return { value: data.values[0].value, date: data.values[0].date };
}

const MetricValue: React.FunctionComponent<{ point: MetricPoint | null }> = ({ point }) =>
  point ? (
    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 6 }}>
      <Text styles={{ root: { fontWeight: 600 } }}>{point.value.toFixed(2)}%</Text>
      <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralTertiary } }}>
        as of {point.date}
      </Text>
    </Stack>
  ) : (
    <Text variant="small" styles={{ root: { color: theme.palette.neutralTertiary } }}>
      #N/A
    </Text>
  );

export interface CompanyInfoProps {
  bank: BankInfo | null;
  onGoToSearch: () => void;
}

const CompanyInfo: React.FunctionComponent<CompanyInfoProps> = ({ bank, onGoToSearch }) => {
  const [roa, setRoa] = useState<MetricPoint | null>(null);
  const [roe, setRoe] = useState<MetricPoint | null>(null);
  const [loading, setLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const handleOpenMap = async () => {
    if (!bank) return;
    setMapError(null);
    try {
      await openBranchMap(bank);
    } catch (err) {
      setMapError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    if (!bank) return;
    let cancelled = false;
    setLoading(true);
    setMetricsError(null);
    setRoa(null);
    setRoe(null);
    Promise.all([fetchLatestMetric(bank.cert, "ROA"), fetchLatestMetric(bank.cert, "ROE")])
      .then(([roaPoint, roePoint]) => {
        if (cancelled) return;
        setRoa(roaPoint);
        setRoe(roePoint);
      })
      .catch((err) => {
        if (cancelled) return;
        setMetricsError(
          err instanceof Error && err.message === "subscription"
            ? "Sign in with an active subscription to see metrics."
            : "Could not load metrics."
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [bank?.cert]);

  if (!bank) {
    return (
      <Stack tokens={{ childrenGap: 8 }}>
        <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>
          No company selected yet.
        </Text>
        <Text variant="small">
          <Link onClick={onGoToSearch}>Search for a bank</Link> to see its details and recent
          performance here.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Text variant="small">
        <Link onClick={onGoToSearch}>← Back to search results</Link>
      </Text>
      <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
        {bank.name}
      </Text>
      <BankInfoCard bank={bank}>
        {loading ? (
          <FieldRow label="ROA / ROE">
            <Spinner size={SpinnerSize.xSmall} />
          </FieldRow>
        ) : metricsError ? (
          <FieldRow label="ROA / ROE">
            <Text variant="small" styles={{ root: { color: theme.palette.neutralTertiary } }}>
              {metricsError}
            </Text>
          </FieldRow>
        ) : (
          <>
            <FieldRow label="ROA">
              <MetricValue point={roa} />
            </FieldRow>
            <FieldRow label="ROE">
              <MetricValue point={roe} />
            </FieldRow>
          </>
        )}
      </BankInfoCard>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <DefaultButton
          text="Map branches"
          iconProps={{ iconName: "MapPin" }}
          onClick={handleOpenMap}
          styles={{ root: { minWidth: 0, height: 28 }, label: { fontSize: 12 } }}
        />
        <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralTertiary } }}>
          Branch locations and deposits from the FDIC Summary of Deposits survey.
        </Text>
      </Stack>
      {mapError && (
        <Text variant="small" styles={{ root: { color: theme.palette.redDark } }}>
          {mapError}
        </Text>
      )}
    </Stack>
  );
};

export default CompanyInfo;
